/**
 * TEMU 发品商品图校验与压缩。
 * 对齐平台发品校验（服饰 skc 轮播图）：
 * - 宽高比 3:4，宽 ≥1340px，高 ≥1785px，≤2MB
 * - 站内统一输出 1340×1785；格式 JPG / JPEG / PNG
 */

import {
  ASPECT_RATIO_TOLERANCE,
  PRODUCT_CAROUSEL_ASPECT,
  isAspectRatioMatch,
} from '@/components/image/fixedAspectCrop';

/** 平台服饰轮播图体积上限（发品错误码 2000014：≤2M） */
export const TEMU_PRODUCT_IMAGE_MAX_BYTES = 2 * 1024 * 1024;
export const TEMU_PRODUCT_IMAGE_ACCEPT = 'image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png';
export const TEMU_PRODUCT_IMAGE_HINT =
  '3:4，宽≥1340 高≥1785（推荐 1340×1785）· 不合规将弹裁剪框 · JPG/PNG · ≤2MB';

/** 服饰类目每个 SKC 的 previewImgUrls（主图+细节图）最少张数 */
export const TEMU_CLOTHING_SKC_CAROUSEL_MIN = 5;

/** 服饰 SKC 轮播图平台上限（主图+细节合计） */
export const TEMU_SKC_CAROUSEL_MAX = 10;

/** SKC 细节图上限（主图单独 1 张，合计不超过 TEMU_SKC_CAROUSEL_MAX） */
export const TEMU_MAX_DETAIL_IMAGES = TEMU_SKC_CAROUSEL_MAX - 1;

/**
 * 组装服饰 SKC 轮播图 URL：主图在前，细节图随后（仅 Temu 平台 URL）。
 * @param {object} skc
 * @param {(img: object|null|undefined) => string} [resolveUrl]
 * @returns {string[]}
 */
export function collectTemuSkcPreviewImgUrls(skc, resolveUrl) {
  const resolve = typeof resolveUrl === 'function'
    ? resolveUrl
    : (img) => (img ? String(img.temuUrl || '').trim() : '');
  const urls = [];
  const push = (img) => {
    const u = String(resolve(img) || '').trim();
    if (u && !urls.includes(u)) urls.push(u);
  };
  push(skc?.mainImage);
  (Array.isArray(skc?.detailImages) ? skc.detailImages : []).forEach(push);
  return urls;
}
const ALLOWED_MIME = new Set(['image/jpeg', 'image/jpg', 'image/png']);
const ALLOWED_EXT = new Set(['jpg', 'jpeg', 'png']);
/** 比例容差（浮点像素取整） */
const RATIO_TOLERANCE = ASPECT_RATIO_TOLERANCE;

function fileExt(name) {
  const base = String(name || '');
  const idx = base.lastIndexOf('.');
  if (idx < 0 || idx === base.length - 1) return '';
  return base.slice(idx + 1).toLowerCase();
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('文件读取失败'));
    reader.readAsDataURL(file);
  });
}

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('图片加载失败'));
    img.src = src;
  });
}

function canvasToBlob(canvas, mime, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mime, quality);
  });
}

/** @returns {string|null} 错误文案 */
export function validateTemuProductImageFormat(file) {
  if (!file) return '请选择图片文件';
  const mime = String(file.type || '').trim().toLowerCase();
  const ext = fileExt(file.name);
  if (mime && !ALLOWED_MIME.has(mime)) return '仅支持 JPG / JPEG / PNG 格式';
  if (ext && !ALLOWED_EXT.has(ext)) return '仅支持 JPG / JPEG / PNG 格式';
  if (!mime && !ext) return '仅支持 JPG / JPEG / PNG 格式';
  return null;
}

export const TEMU_MAIN_WIDTH = 1340;
export const TEMU_MAIN_HEIGHT = 1785;
/** 主图/细节图：平台服饰轮播最小边（宽≥1340、高≥1785） */
export const TEMU_MAIN_DETAIL_MIN_W = 1340;
export const TEMU_MAIN_DETAIL_MIN_H = 1785;
/**
 * 裁剪源区域下限（短于平台输出边时由 output 放大到 1340×1785）。
 * 过严会导致常见手机图直接报错、不弹裁剪框。
 */
export const TEMU_CROP_SOURCE_MIN = 800;
/** @deprecated 兼容旧引用，等同平台最小宽 */
export const TEMU_MAIN_DETAIL_MIN = TEMU_MAIN_DETAIL_MIN_W;
export const TEMU_MAIN_DETAIL_MAX = 2200;
export const TEMU_SQUARE_MIN = 800;
export const TEMU_SQUARE_MAX = 2200;
/** 素材图（materialImgUrl）平台：1:1，边长 ≥800，≤2MB */
export const TEMU_MATERIAL_SIDE = TEMU_SQUARE_MIN;
export const TEMU_MATERIAL_IMAGE_HINT =
  '1:1，边长≥800px（推荐 800×800）· 不合规将弹裁剪框 · JPG/PNG · ≤2MB';

/**
 * 外包装图（imageBizType=1）：官方 Product Basic Introduction 只规定货品轮播图 3:4/1:1，
 * 外包装仅要求 1–6 张含正拍/侧拍/俯拍，**未要求 3:4**。
 * 上传接口通用限制：JPG/PNG、宽高均≥800px、≤3MB（对齐错误码 150010058 / 150010056）。
 */
export const TEMU_OUTER_PACKAGE_IMAGE_MAX_BYTES = 3 * 1024 * 1024;
export const TEMU_OUTER_PACKAGE_MIN_EDGE = 800;
export const TEMU_OUTER_PACKAGE_IMAGE_HINT =
  'JPG/PNG · 宽高均≥800px · ≤3MB · 1–6 张须含正拍/侧拍/俯拍（外包装不要求 3:4）';

/**
 * 外包装图尺寸：仅校验最小边，不校验固定比例。
 * @returns {string|null}
 */
export function validateTemuOuterPackageImageDimensions(width, height) {
  const w = Number(width) || 0;
  const h = Number(height) || 0;
  if (w <= 0 || h <= 0) {
    return `外包装图尺寸无效，须宽高均≥${TEMU_OUTER_PACKAGE_MIN_EDGE}px`;
  }
  if (w < TEMU_OUTER_PACKAGE_MIN_EDGE || h < TEMU_OUTER_PACKAGE_MIN_EDGE) {
    return `外包装图过小：须宽高均≥${TEMU_OUTER_PACKAGE_MIN_EDGE}px（当前 ${w}×${h}）`;
  }
  return null;
}

/**
 * 外包装图：校验格式与最小边，按原比例压到 ≤3MB（不强制 3:4，不弹主图裁剪框）。
 */
export async function prepareTemuOuterPackageImage(file) {
  const formatError = validateTemuProductImageFormat(file);
  if (formatError) throw new Error(formatError);

  const dataUrl = await readFileAsDataUrl(file);
  const img = await loadImageElement(dataUrl);
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  if (width <= 0 || height <= 0) throw new Error('无法读取图片尺寸');

  const dimError = validateTemuOuterPackageImageDimensions(width, height);
  if (dimError) throw new Error(dimError);

  if (file.size <= TEMU_OUTER_PACKAGE_IMAGE_MAX_BYTES) {
    return file;
  }
  return compressTemuProductImageToLimit(file, TEMU_OUTER_PACKAGE_IMAGE_MAX_BYTES);
}

/**
 * 素材图尺寸：比例 1:1，宽高均 ≥800。
 * @returns {string|null}
 */
export function validateTemuMaterialImageDimensions(width, height) {
  const w = Number(width) || 0;
  const h = Number(height) || 0;
  if (w <= 0 || h <= 0) {
    return '素材图尺寸不符合平台要求：须为 1:1，边长≥800px';
  }
  if (!isAspectRatioMatch(w, h, 1, RATIO_TOLERANCE)) {
    return '素材图尺寸不符合平台要求：比例须为 1:1';
  }
  if (w < TEMU_MATERIAL_SIDE || h < TEMU_MATERIAL_SIDE) {
    return `素材图过小：须边长≥${TEMU_MATERIAL_SIDE}px（当前 ${w}×${h}）`;
  }
  return null;
}

/**
 * 素材图绘制：居中取最大正方形，输出边长 clamp 到 [800, 2200]。
 */
export function resolveTemuMaterialDraw(width, height) {
  const w = Math.max(1, Number(width) || 0);
  const h = Math.max(1, Number(height) || 0);
  const cropSide = Math.min(w, h);
  const sx = Math.floor((w - cropSide) / 2);
  const sy = Math.floor((h - cropSide) / 2);
  let out = cropSide;
  if (out < TEMU_MATERIAL_SIDE) out = TEMU_MATERIAL_SIDE;
  if (out > TEMU_SQUARE_MAX) out = TEMU_SQUARE_MAX;
  return { sx, sy, sw: cropSide, sh: cropSide, dw: out, dh: out };
}

/**
 * 校验格式；要求已是 1:1≥800（否则抛 NEED_ASPECT_CROP）；缩放到合格正方形并压缩 ≤2MB。
 */
export async function prepareTemuMaterialImage(file, { requireAspect = true } = {}) {
  const formatError = validateTemuProductImageFormat(file);
  if (formatError) throw new Error(formatError);

  const dataUrl = await readFileAsDataUrl(file);
  const img = await loadImageElement(dataUrl);
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  if (width <= 0 || height <= 0) throw new Error('无法读取图片尺寸');

  if (requireAspect) {
    const dimError = validateTemuMaterialImageDimensions(width, height);
    if (dimError) {
      const err = new Error(dimError);
      err.code = 'NEED_ASPECT_CROP';
      throw err;
    }
  }

  const draw = resolveTemuMaterialDraw(width, height);
  const canvas = document.createElement('canvas');
  canvas.width = draw.dw;
  canvas.height = draw.dh;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('无法处理图片');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, draw.dw, draw.dh);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, draw.sx, draw.sy, draw.sw, draw.sh, 0, 0, draw.dw, draw.dh);

  const baseName = (file.name || 'material').replace(/\.[^.]+$/, '') || 'material';
  let quality = 0.92;
  while (quality >= 0.35) {
    const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
    if (blob && blob.size <= TEMU_PRODUCT_IMAGE_MAX_BYTES) {
      return new File([blob], `${baseName}-material.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
    }
    quality -= 0.07;
  }
  throw new Error('素材图压缩后仍超过 2MB，请换一张更小的图');
}

/**
 * 站内主图/细节图（对齐平台 2000014）：
 * - 比例必须 3:4
 * - 宽 ≥1340、高 ≥1785
 * @returns {string|null}
 */
export function validateTemuProductImageDimensions(width, height) {
  const w = Number(width) || 0;
  const h = Number(height) || 0;
  if (w <= 0 || h <= 0) {
    return '图片尺寸不符合平台要求：须为 3:4，宽≥1340px、高≥1785px';
  }
  if (!isAspectRatioMatch(w, h, PRODUCT_CAROUSEL_ASPECT, RATIO_TOLERANCE)) {
    return '图片尺寸不符合平台要求：主图/细节图比例须为 3:4';
  }
  if (w < TEMU_MAIN_DETAIL_MIN_W || h < TEMU_MAIN_DETAIL_MIN_H) {
    return `图片过小：须宽≥${TEMU_MAIN_DETAIL_MIN_W}px、高≥${TEMU_MAIN_DETAIL_MIN_H}px（当前 ${w}×${h}）`;
  }
  return null;
}

/**
 * 主图/细节图绘制目标：统一输出 1340×1785（3:4）。
 * 已是 3:4 则整图缩放；否则居中取 3:4 区域（上传 UI 应先弹裁剪框）。
 */
export function resolveTemuMainDetailDraw(width, height) {
  const w = Math.max(1, Number(width) || 0);
  const h = Math.max(1, Number(height) || 0);
  const targetRatio = TEMU_MAIN_WIDTH / TEMU_MAIN_HEIGHT;
  const current = w / h;
  let sx = 0;
  let sy = 0;
  let sw = w;
  let sh = h;
  if (Math.abs(current - targetRatio) > RATIO_TOLERANCE) {
    if (current > targetRatio) {
      sh = h;
      sw = Math.max(1, Math.round(h * targetRatio));
      sx = Math.max(0, Math.floor((w - sw) / 2));
    } else {
      sw = w;
      sh = Math.max(1, Math.round(w / targetRatio));
      sy = Math.max(0, Math.floor((h - sh) / 2));
    }
  }
  return { sx, sy, sw, sh, dw: TEMU_MAIN_WIDTH, dh: TEMU_MAIN_HEIGHT };
}

/**
 * 在保持像素不变的前提下压缩到 ≤ maxBytes（默认 2MB）。
 * 超出时逐步降低 JPEG 质量；无法压到限制则抛错。
 */
export async function compressTemuProductImageToLimit(file, maxBytes = TEMU_PRODUCT_IMAGE_MAX_BYTES) {
  if (!file) throw new Error('无效图片');
  if (file.size <= maxBytes) return file;

  const dataUrl = await readFileAsDataUrl(file);
  const img = await loadImageElement(dataUrl);
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth || img.width;
  canvas.height = img.naturalHeight || img.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('无法压缩图片');

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0);

  const baseName = (file.name || 'product').replace(/\.[^.]+$/, '') || 'product';
  let quality = 0.92;
  let best = null;

  while (quality >= 0.35) {
    const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
    if (blob) {
      best = blob;
      if (blob.size <= maxBytes) {
        return new File([blob], `${baseName}.jpg`, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });
      }
    }
    quality -= 0.07;
  }

  if (best && best.size < file.size) {
    // 已尽量压缩但仍超限
    throw new Error(`图片压缩后仍超过 ${Math.round(maxBytes / (1024 * 1024))}MB，请换一张更小的图`);
  }
  throw new Error(`图片超过 ${Math.round(maxBytes / (1024 * 1024))}MB 且无法有效压缩，请换一张更小的图`);
}

/**
 * 由主图 File 居中裁成 1:1 方块图，边长落在 900–2200，体积 ≤3MB。
 * 不依赖远程 URL，避免跨域污染 canvas。
 */
export async function cropFileToTemuSquare(file) {
  if (!file) throw new Error('无效主图');
  const dataUrl = await readFileAsDataUrl(file);
  const img = await loadImageElement(dataUrl);
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  if (width <= 0 || height <= 0) throw new Error('无法读取主图尺寸');

  const cropSide = Math.min(width, height);
  const sx = Math.floor((width - cropSide) / 2);
  const sy = Math.floor((height - cropSide) / 2);

  let outSide = cropSide;
  if (outSide < 900) outSide = 900;
  if (outSide > 2200) outSide = 2200;

  const canvas = document.createElement('canvas');
  canvas.width = outSide;
  canvas.height = outSide;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('无法生成方块图');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, outSide, outSide);
  ctx.drawImage(img, sx, sy, cropSide, cropSide, 0, 0, outSide, outSide);

  const baseName = (file.name || 'square').replace(/\.[^.]+$/, '') || 'square';
  let quality = 0.92;
  while (quality >= 0.35) {
    const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
    if (blob && blob.size <= TEMU_PRODUCT_IMAGE_MAX_BYTES) {
      return new File([blob], `${baseName}-square.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
    }
    quality -= 0.07;
  }
  throw new Error('方块图压缩后仍超过 3MB');
}

/** 色块图输出边长（平台要求 80×80） */
export const TEMU_COLOR_BLOCK_SIDE = 80;
/**
 * 从主图短边取中心区域的比例（局部色块，非整张商品）。
 * 过小易噪点，过大会带入轮廓；约 28% 贴近商家后台「截取局部色块」习惯。
 */
export const TEMU_COLOR_BLOCK_CENTER_RATIO = 0.28;

/**
 * 色块图（image_type=6）：从主图正中裁出局部正方形色块，缩放到 80×80 JPG。
 * 用于搜索/列表色块展示；上传时走 upload-pic type=6 换 TEMU URL。
 */
export async function cropFileToTemuColorBlock(file) {
  if (!file) throw new Error('无效主图');
  const dataUrl = await readFileAsDataUrl(file);
  const img = await loadImageElement(dataUrl);
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  if (width <= 0 || height <= 0) throw new Error('无法读取主图尺寸');

  const minSide = Math.min(width, height);
  // 至少取 80px 源区域，避免极小图放大发糊
  const cropSide = Math.max(
    TEMU_COLOR_BLOCK_SIDE,
    Math.floor(minSide * TEMU_COLOR_BLOCK_CENTER_RATIO),
  );
  const sx = Math.floor((width - cropSide) / 2);
  const sy = Math.floor((height - cropSide) / 2);

  const out = TEMU_COLOR_BLOCK_SIDE;
  const canvas = document.createElement('canvas');
  canvas.width = out;
  canvas.height = out;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('无法生成色块图');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, out, out);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, sx, sy, cropSide, cropSide, 0, 0, out, out);

  const baseName = (file.name || 'color-block').replace(/\.[^.]+$/, '') || 'color-block';
  let quality = 0.92;
  while (quality >= 0.5) {
    const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
    if (blob && blob.size <= TEMU_PRODUCT_IMAGE_MAX_BYTES) {
      return new File([blob], `${baseName}-color-block.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
    }
    quality -= 0.07;
  }
  throw new Error('色块图生成失败');
}

/**
 * 校验格式；要求已是 3:4（否则抛错，由上传层弹裁剪框）；
 * 缩放到 1340×1785 并压缩到 ≤2MB（平台发品上限）。
 */
export async function prepareTemuProductImage(file, { requireAspect = true } = {}) {
  const formatError = validateTemuProductImageFormat(file);
  if (formatError) throw new Error(formatError);

  const dataUrl = await readFileAsDataUrl(file);
  const img = await loadImageElement(dataUrl);
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  if (width <= 0 || height <= 0) throw new Error('无法读取图片尺寸');

  if (requireAspect) {
    const dimError = validateTemuProductImageDimensions(width, height);
    if (dimError) {
      const err = new Error(dimError);
      err.code = 'NEED_ASPECT_CROP';
      throw err;
    }
  }

  const draw = resolveTemuMainDetailDraw(width, height);
  const canvas = document.createElement('canvas');
  canvas.width = draw.dw;
  canvas.height = draw.dh;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('无法处理图片');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, draw.dw, draw.dh);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, draw.sx, draw.sy, draw.sw, draw.sh, 0, 0, draw.dw, draw.dh);

  const baseName = (file.name || 'product').replace(/\.[^.]+$/, '') || 'product';
  let quality = 0.92;
  while (quality >= 0.35) {
    const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
    if (blob && blob.size <= TEMU_PRODUCT_IMAGE_MAX_BYTES) {
      return new File([blob], `${baseName}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
    }
    quality -= 0.07;
  }
  throw new Error('图片压缩后仍超过 2MB，请换一张更小的图');
}
